import {
  Components,
  PaletteColor,
  PaletteOptions,
  Theme,
  alpha,
  createTheme,
  lighten,
} from "@mui/material";
import { blueGrey, grey } from "@mui/material/colors";

import { encampBrandColors } from "./colors";
import { brandColors } from "./util";

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    encampBlue: true;
    sapphire: true;
    tealLight: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    encampBlue: true;
    encampGreen: true;
    encampRed: true;
    grey50: true;
  }
}

// Add custom properties to the theme to be used throughout the app.
//
//  For example, theme.palette.grey50 will provide a light grey for light mode
// and a dark grey for dark mode.
declare module "@mui/material/styles" {
  interface NavPalette {
    background: string;
    scrollThumb: string;
    scrollThumbHover: string;
    /** Resting (non-active) customer nav icon color. */
    icon: string;
    /** Resting (non-active) staff nav icon color. */
    staffIcon: string;
    /** Selected-item accent color; also the nav hover accent. */
    selected: string;
    /** Active-item background — solid pill in light, faint teal wash in dark. */
    selectedBg: string;
    /** Active-item icon/text — white in light, teal accent in dark. */
    selectedFg: string;
    /**
     * Subtle divider for customer section headings and group connectors.
     * Recedes against the Sapphire rail; pumpkin (`staffIcon`) stays reserved
     * as the staff accent so the brand "pop" color isn't spent on dividers.
     */
    divider: string;
  }
  interface Palette {
    /** Encamp's grey.50, which will also provide a grey for dark mode */
    grey50: PaletteColor;
    encampBlue: PaletteColor;
    /** Encamp brand green (teal). Use for "active"/"healthy" status indicators. */
    encampGreen: PaletteColor;
    /** Encamp brand red. Use for error/disabled/former status indicators. */
    encampRed: PaletteColor;
    /**
     * The Encamp 2/26 Brand Guidelines palette, exposed for theme-driven use.
     * Usage hierarchy from the guidelines: ~60% primaries (navy, pumpkin,
     * amazonGreen, sapphire), ~30% secondaries (white, lightGray, independence,
     * richBlack), ~10% accents (cyan, yellowGreen — highlights only, sparingly).
     */
    brand: typeof encampBrandColors;
    /** Blue Sapphire as a usable component color (e.g. `<Button color="sapphire">`). */
    sapphire: PaletteColor;
    /** A lighter, brighter teal as a component color — for contained CTAs that sit on a near-white surface (e.g. Scout's suggestion buttons). */
    tealLight: PaletteColor;
    nav: NavPalette;
    /** Page/canvas background for landing pages, dashboards, etc. (per scheme). */
    canvas: string;
    /** Thumb color for custom (non-nav) content scrollbars (per scheme). */
    scrollbarThumb: string;
    /** Subtle raised-surface tint for cards/sections (per scheme). */
    surfaceSubtle: string;
    /** Scout chat page background — a warm, paper-like canvas (per scheme). */
    scoutCanvas: string;
    /** Scout user-message bubble fill — a warm surface a step off the canvas (per scheme). */
    scoutUserBubble: string;
  }
  // allow configuration using `createTheme()`. The Encamp tokens are optional
  // here (per MUI convention for custom palette keys) so minimal test themes
  // can omit them; the app theme below always provides them, which is what the
  // required `Palette` declarations above describe.
  interface PaletteOptions {
    /** Encamp's grey.50, which will also provide a grey for dark mode */
    grey50: PaletteColor;
    encampBlue: PaletteColor;
    encampGreen: PaletteColor;
    encampRed: PaletteColor;
    /** The Encamp 2/26 Brand Guidelines palette. */
    brand?: typeof encampBrandColors;
    /** Blue Sapphire as a usable component color (e.g. `<Button color="sapphire">`). */
    sapphire?: PaletteColor;
    /** A lighter, brighter teal as a component color (e.g. `<Button color="tealLight">`). */
    tealLight?: PaletteColor;
    nav?: NavPalette;
    /** Page/canvas background for landing pages, dashboards, etc. (per scheme). */
    canvas?: string;
    /** Thumb color for custom (non-nav) content scrollbars (per scheme). */
    scrollbarThumb?: string;
    /** Subtle raised-surface tint for cards/sections (per scheme). */
    surfaceSubtle?: string;
    /** Scout chat page background — a warm, paper-like canvas (per scheme). */
    scoutCanvas?: string;
    /** Scout user-message bubble fill — a warm surface a step off the canvas (per scheme). */
    scoutUserBubble?: string;
  }
}

// Builds a full PaletteColor (light/dark/contrastText derived by MUI) from a
// single brand hex, so promoting a brand color to a component color — e.g.
// `<Button color="sapphire">` — is one line here plus the relevant
// `*PropsColorOverrides` flag above. (MUI `color` props require flat palette
// keys; nested names like `brand.sapphire` can't be used in class generation.)
const { augmentColor } = createTheme().palette;

// A lighter, brighter teal than Blue Sapphire, for accents and CTAs on
// near-white surfaces where sapphire reads too dark (Scout suggestion buttons,
// the `info` slot).
const TEAL_LIGHT = "#1B8593";

// Palette values shared by both color schemes (mode-invariant).
const sharedPalette = {
  // The full 2/26 brand palette, mirrored onto the theme. This token is
  // OPTIONAL in PaletteOptions (minimal/test themes may omit it), so reading
  // `theme.palette.brand.x` is unsafe — for a fixed brand color, import
  // `encampBrandColors` directly instead. See the access convention in
  // colors.ts. `primary` and `info` ARE remapped to brand teal, but PER SCHEME
  // (see buildLightPalette/buildDarkPalette): deep Sapphire on light, a lightened teal on
  // dark so primary-as-text/links/icons stays readable on a dark background —
  // mirroring MUI's own convention of a lighter primary in dark mode. The other
  // semantic slots (`secondary`, `success`, `warning`, `error`) keep their
  // defaults.
  brand: encampBrandColors,
  // Blue Sapphire as a first-class component color: solid brand CTAs (the
  // sign-in buttons) get `<Button color="sapphire">`.
  sapphire: augmentColor({ color: { main: encampBrandColors.sapphire } }),
  // A lighter, brighter teal for contained CTAs on near-white surfaces (Scout's
  // suggestion buttons) — sapphire reads too dark there. MUI derives hover/contrast.
  tealLight: augmentColor({ color: { main: TEAL_LIGHT } }),
  secondary: {
    main: brandColors["encamp-orange"][500],
    light: brandColors["encamp-orange"][200],
    dark: brandColors["encamp-orange"][700],
    contrastText: brandColors["neutral-gray"][50],
  },
  encampBlue: {
    main: "#517BC6",
    light: "#517BC61F",
    dark: brandColors["encamp-blue"][700],
    contrastText: brandColors["neutral-gray"][50],
  },
  encampGreen: {
    main: brandColors["encamp-green"][500],
    light: brandColors["encamp-green"][200],
    dark: brandColors["encamp-green"][700],
    contrastText: brandColors["neutral-gray"][50],
  },
  encampRed: {
    main: brandColors["encamp-red"][500],
    light: brandColors["encamp-red"][200],
    dark: brandColors["encamp-red"][700],
    contrastText: brandColors["neutral-gray"][50],
  },
};

// ---------------------------------------------------------------------------
// Theme variants
//
// The visual theme is produced by `createEncampTheme(variant)`. Both variants
// define the SAME token names, so components — which read `theme.palette.primary`,
// `theme.palette.nav.*`, `theme.palette.scoutCanvas`, `surfaceSubtle`, etc. —
// render correctly under either without changes. Turning the brand refresh on/
// off is therefore a single config switch, not a component rewrite.
//   - "brand2026": the Encamp 2/26 brand refresh (teal primary, sapphire nav,
//                  Claude-style Scout canvas). The default.
//   - "classic":   a neutral, MUI-default-ish baseline (blue primary, slate nav,
//                  grey surfaces) — a coherent alternative for an A/B toggle;
//                  tune to taste if an exact legacy match is needed.
// Only the values that genuinely differ between variants live in the config;
// structural tokens (text, grey50, scrollbars, surfaceSubtle) are shared below.
// ---------------------------------------------------------------------------

export type ThemeVariant = "brand2026" | "classic";

type VariantColors = {
  /** `primary.main` per color scheme. */
  primaryLight: string;
  primaryDark: string;
  /** `info.main` per color scheme. */
  infoLight: string;
  infoDark: string;
  /**
   * Left-nav rail / resting icon / selection accent, per scheme. Dark mode is
   * designed dark-first: the rail is a NEUTRAL dark surface with neutral resting
   * icons, and the brand color is spent only on the selection accent.
   */
  navBackgroundLight: string;
  navBackgroundDark: string;
  navIconLight: string;
  navIconDark: string;
  navSelectedLight: string;
  navSelectedDark: string;
  /** Color the nav section divider is derived from (alpha 0.45), per scheme. */
  navDividerBaseLight: string;
  navDividerBaseDark: string;
  /** App canvas (landing/dashboard backgrounds) per scheme. */
  canvasLight: string;
  canvasDark: string;
  /**
   * Dark raised-surface level — cards/paper/nav/bubbles all use this single
   * value (one step up from `canvasDark`) so dark mode is two cohesive surface
   * levels instead of a patchwork of mismatched darks.
   */
  surfaceDark: string;
  /** Scout chat canvas + user-message bubble per scheme. */
  scoutCanvasLight: string;
  scoutCanvasDark: string;
  scoutUserBubbleLight: string;
  scoutUserBubbleDark: string;
};

// Light muted teal for resting customer nav elements in the brand variant.
const NAV_CUSTOMER_TEAL = "#81B7BF";

const BRAND_2026_COLORS: VariantColors = {
  // Deep Sapphire on light (~7:1); lightened for dark so primary-as-text/links
  // stays readable. See the Encamp 2/26 brand guidelines (Blue Sapphire #0F5F6B).
  primaryLight: encampBrandColors.sapphire,
  // Dark: lightening Sapphire toward white desaturates it to a muddy grey-teal,
  // so use the brand Cyan accent — vivid, clearly teal, ~8:1 on a dark surface,
  // and consistent with the nav's cyan selection.
  primaryDark: encampBrandColors.cyan,
  infoLight: TEAL_LIGHT,
  infoDark: lighten(TEAL_LIGHT, 0.4),
  // Light: brand Sapphire teal rail with muted-teal icons. Dark: neutral dark
  // slate rail with neutral-grey icons — teal/cyan only on the selection accent.
  navBackgroundLight: encampBrandColors.sapphire,
  navBackgroundDark: "#23272B",
  navIconLight: NAV_CUSTOMER_TEAL,
  navIconDark: "#9AA0A6",
  navSelectedLight: encampBrandColors.cyan,
  navSelectedDark: encampBrandColors.cyan,
  navDividerBaseLight: NAV_CUSTOMER_TEAL,
  navDividerBaseDark: "#9AA0A6",
  canvasLight: encampBrandColors.lightGray,
  // Dark: one app/page background (L0) + one raised surface (L1, == nav) for a
  // calm, low-variation dark palette in the same neutral-slate hue.
  canvasDark: "#17191C",
  surfaceDark: "#23272B",
  scoutCanvasLight: "#FCFCFC",
  scoutCanvasDark: "#17191C",
  scoutUserBubbleLight: "#ECECE8",
  scoutUserBubbleDark: "#23272B",
};

const CLASSIC_COLORS: VariantColors = {
  // The pre-refresh ("legacy") theme customers keep seeing: Deep Navy nav,
  // MUI-default blue primary, encampBlue selection. Reproduces the dev theme's
  // navy nav (#051E3C) + #517BC6 selection + #878D99 icons.
  primaryLight: "#1976d2",
  primaryDark: "#90caf9",
  infoLight: "#0288d1",
  infoDark: "#4fc3f7",
  navBackgroundLight: "#051E3C",
  navBackgroundDark: blueGrey[900],
  navIconLight: "#878D99",
  navIconDark: "#878D99",
  navSelectedLight: "#517BC6",
  navSelectedDark: "#517BC6",
  navDividerBaseLight: "#596376",
  navDividerBaseDark: "#596376",
  canvasLight: grey[50],
  canvasDark: blueGrey[900],
  surfaceDark: blueGrey[800],
  scoutCanvasLight: grey[50],
  scoutCanvasDark: brandColors["encamp-gray"][900],
  scoutUserBubbleLight: grey[100],
  scoutUserBubbleDark: brandColors["encamp-gray"][600],
};

const VARIANT_COLORS: Record<ThemeVariant, VariantColors> = {
  brand2026: BRAND_2026_COLORS,
  classic: CLASSIC_COLORS,
};

/**
 * Default variant for the exported themes and any caller that doesn't pass one.
 * The 2/26 brand refresh ("brand2026") is the shipped customer theme.
 * Staff can still switch back to ("classic") via PaletteVariantProvider for
 * comparison — see EncampThemeProvider.
 */
export const ACTIVE_THEME_VARIANT: ThemeVariant = "brand2026";

// The nav is a fixed brand surface in both modes; only the scroll-thumb tints
// contrast the surface differently per scheme.
function buildNav(cfg: VariantColors, scheme: "light" | "dark") {
  const isLight = scheme === "light";
  const navSelected = isLight ? cfg.navSelectedLight : cfg.navSelectedDark;
  return {
    background: isLight ? cfg.navBackgroundLight : cfg.navBackgroundDark,
    icon: isLight ? cfg.navIconLight : cfg.navIconDark,
    // Pumpkin distinguishes staff controls from customer nav (both variants).
    staffIcon: encampBrandColors.pumpkin,
    selected: navSelected,
    // Light: solid pill + white text. Dark: dark-first "subtle highlight" — a
    // faint teal wash with teal text/icon, so the brand color is a quiet accent.
    selectedBg: isLight ? navSelected : alpha(navSelected, 0.18),
    selectedFg: isLight ? "#FFFFFF" : navSelected,
    // Section dividers / group connectors: the resting nav tint dialed back so
    // it reads as quiet structure rather than an accent.
    divider: alpha(isLight ? cfg.navDividerBaseLight : cfg.navDividerBaseDark, 0.45),
    scrollThumb: isLight ? "#878D99" : alpha(brandColors["encamp-gray"][50], 0.38),
    scrollThumbHover: isLight ? "#A0A6B2" : alpha(brandColors["encamp-gray"][50], 0.52),
  };
}

function buildLightPalette(cfg: VariantColors): PaletteOptions {
  return {
    ...sharedPalette,
    primary: augmentColor({ color: { main: cfg.primaryLight } }),
    info: augmentColor({ color: { main: cfg.infoLight } }),
    text: {
      primary: brandColors["encamp-gray"][800],
      secondary: brandColors["encamp-gray"][500],
    },
    grey50: {
      main: brandColors["encamp-gray"][50],
      light: brandColors["encamp-gray"][50],
      dark: brandColors["encamp-gray"][100],
      contrastText: brandColors["neutral-gray"][900],
    },
    nav: buildNav(cfg, "light"),
    canvas: cfg.canvasLight,
    // = alpha(text.primary, 0.24) for the light scheme.
    scrollbarThumb: alpha(brandColors["encamp-gray"][800], 0.24),
    surfaceSubtle: grey[100],
    scoutCanvas: cfg.scoutCanvasLight,
    scoutUserBubble: cfg.scoutUserBubbleLight,
  };
}

function buildDarkPalette(cfg: VariantColors): PaletteOptions {
  return {
    ...sharedPalette,
    primary: augmentColor({ color: { main: cfg.primaryDark } }),
    info: augmentColor({ color: { main: cfg.infoDark } }),
    text: {
      primary: brandColors["encamp-gray"][50],
      secondary: brandColors["encamp-gray"][500],
    },
    grey50: {
      main: brandColors["encamp-gray"][700],
      light: brandColors["encamp-gray"][600],
      dark: brandColors["encamp-gray"][800],
      contrastText: brandColors["neutral-gray"][50],
    },
    // One app background (L0) + one raised surface (L1) so cards/menus/dialogs
    // all share the nav's surface level instead of MUI's default #121212 — the
    // source of the patchwork "uneven" look.
    background: { default: cfg.canvasDark, paper: cfg.surfaceDark },
    nav: buildNav(cfg, "dark"),
    canvas: cfg.canvasDark,
    // = alpha(text.primary, 0.34) for the dark scheme.
    scrollbarThumb: alpha(brandColors["encamp-gray"][50], 0.34),
    surfaceSubtle: cfg.surfaceDark,
    scoutCanvas: cfg.scoutCanvasDark,
    scoutUserBubble: cfg.scoutUserBubbleDark,
  };
}

/**
 * Single static theme with light/dark color schemes.
 *
 * `cssVariables` is intentionally left OFF: with `colorSchemes` only, MUI merges
 * the active scheme's palette into the theme and re-renders on mode change, so
 * `theme.palette.mode` and all palette reads stay reactive — no component-level
 * `applyStyles`/`theme.vars` migration is required. Enabling `cssVariables`
 * (the zero-re-render optimization) is a deliberate follow-up that requires
 * converting the `palette.mode` branches.
 */
const components: Components<Theme> = {
  // https://github.com/mui/material-ui/issues/13253#issuecomment-752302891
  MuiDialog: {
    styleOverrides: {
      paper: {
        "& > form": {
          display: "contents",
        },
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        paddingTop: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(3),
      }),
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1.5),
      }),
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        paddingBottom: theme.spacing(0),
      }),
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        whiteSpace: "nowrap", // Prevent text from wrapping in all buttons
      },
    },
  },

  MuiToggleButton: {
    styleOverrides: {
      root: {
        whiteSpace: "nowrap",
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        "& .input-tooltip": {
          pointerEvents: "auto", // Enables pointer events when the tooltip is inside the input
        },
      },
    },
  },

  MuiTooltip: {
    defaultProps: {
      // Non-interactive by default: tooltip hides immediately when the
      // cursor leaves the trigger, even if the cursor lands on the
      // tooltip body. Prevents the tooltip from blocking row-hover state
      // in DataGrid action cells and column-header sort/truncation
      // tooltips. Components that need users to mouse into the tooltip
      // (e.g. InputTooltip with its leaveDelay) must opt out with
      // `disableInteractive={false}`.
      disableInteractive: true,
    },
    styleOverrides: {
      tooltip: ({ theme }) => ({
        "& a, & a:visited": {
          color: theme.palette.primary.main,
          textDecoration: "underline",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      }),
    },
  },
};

/**
 * Full light+dark theme for the given brand variant. Components read theme
 * tokens, so the variant choice never reaches them — to A/B the old look, render
 * `createEncampTheme("classic")` at the ThemeProvider (e.g. behind a setting).
 */
export function createEncampTheme(variant: ThemeVariant = ACTIVE_THEME_VARIANT) {
  const cfg = VARIANT_COLORS[variant];
  return createTheme({
    components,
    colorSchemes: {
      light: { palette: buildLightPalette(cfg) },
      dark: { palette: buildDarkPalette(cfg) },
    },
  });
}

/**
 * Light-locked theme for unauthenticated/public surfaces (login, error, 404).
 * Dark mode is an authenticated-app feature reachable only via the in-app
 * toggle, so public pages always render light — and a `CssBaseline` under this
 * theme gives them an explicit light `body` background instead of letting a
 * persisted/OS dark mode bleed through behind a light container.
 */
export function createEncampLightTheme(variant: ThemeVariant = ACTIVE_THEME_VARIANT) {
  return createTheme({ components, palette: buildLightPalette(VARIANT_COLORS[variant]) });
}

export const encampTheme = createEncampTheme();
export const encampLightTheme = createEncampLightTheme();
